import { useMemo } from "react";
import { Check, ChevronDown } from "lucide-react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";

interface MultiSelectProps {
  options: { value: string; label: string }[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

export const MultiSelect = ({ options, value, onChange, placeholder = "Select options" }: MultiSelectProps) => {
  const selection = useMemo(() => new Set(value), [value]);

  const toggle = (selected: string) => {
    const next = new Set(selection);
    next.has(selected) ? next.delete(selected) : next.add(selected);
    onChange(Array.from(next));
  };

  return (
    <PopoverPrimitive.Root>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm text-white hover:border-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coderide-light-blue"
        >
          <span className="truncate text-left text-white/80">
            {selection.size > 0 ? `${selection.size} selected` : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 text-white/50" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-50 w-64 rounded-lg border border-white/10 bg-[#101015] p-2 shadow-xl"
        >
          <div className="max-h-48 space-y-1 overflow-y-auto">
            {options.map((option) => {
              const checked = selection.has(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggle(option.value)}
                  className={cn(
                    "flex w-full items-center justify-between rounded-md px-2 py-2 text-sm text-white/80 hover:bg-white/10",
                    checked && "bg-white/10",
                  )}
                >
                  <span>{option.label}</span>
                  {checked && <Check className="h-4 w-4 text-coderide-light-blue" />}
                </button>
              );
            })}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};
